import { Link, useNavigate } from "react-router-dom";
import { Map, LogOut, ChevronDown, Crown, CreditCard, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function ProBadge({ sub, onClick }) {
  if (sub.status === "active") {
    return (
      <button
        onClick={onClick}
        data-testid="header-pro-badge"
        title={`${sub.tier?.toUpperCase()} · ${sub.days_left} days remaining`}
        className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition"
      >
        <Crown className="w-3 h-3" />
        Pro
      </button>
    );
  }
  if (sub.status === "trial") {
    return (
      <button
        onClick={onClick}
        data-testid="header-trial-badge"
        title={`Trial · ${sub.days_left} days remaining`}
        className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium hover:bg-amber-100 transition"
      >
        <Sparkles className="w-3 h-3" />
        Trial · {sub.days_left}d left
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      data-testid="header-upgrade-badge"
      className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition"
    >
      <Sparkles className="w-3 h-3" />
      Upgrade
    </button>
  );
}

export default function Header({ variant = "default" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="app-header"
      className="h-20 w-full flex-none z-50 bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-slate-200 sticky top-0"
    >
      <div className="h-full max-w-screen-2xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src="/brand/ctc-logo-240.png"
            alt="Coast To Coast Permits — Nationwide Commercial Permitting"
            data-testid="header-logo-img"
            className="h-16 w-auto object-contain transition-transform group-hover:scale-105"
            width="51"
            height="64"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-outfit font-bold text-slate-900 text-base tracking-tight">Coast To Coast Permitting</span>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">AHJ · Cities · Counties · Townships · Metros</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <Link
            to="/services"
            data-testid="nav-services-link"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Permitting Services
          </Link>
          <Link
            to="/data-center"
            data-testid="nav-data-center-link"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Data Centers
          </Link>
          <Link
            to="/hotels"
            data-testid="nav-hotels-link"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Hotels
          </Link>
          <Link
            to="/qa"
            data-testid="nav-qa-link"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Q&amp;A
          </Link>
          <Link
            to="/ahj-verify"
            data-testid="nav-ahj-verify-link"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            AHJ Verify
          </Link>
          <Link
            to="/due-diligence"
            data-testid="nav-due-diligence-link"
            className="text-sm font-medium text-indigo-700 hover:text-indigo-900 transition-colors inline-flex items-center gap-1"
          >
            <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
            Due Diligence
          </Link>
          {user && (
            <Link
              to="/chat"
              data-testid="nav-chat-link"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              Assistant
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {!user && (
            <>
              <Button
                data-testid="header-login-btn"
                onClick={() => navigate("/login")}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-sm"
              >
                Sign in
              </Button>
            </>
          )}
          {user && (
            <>
              {user.subscription && (
                <ProBadge sub={user.subscription} onClick={() => navigate(user.subscription.status === "active" ? "/billing" : "/pricing")} />
              )}
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 text-slate-700 hover:bg-slate-100"
                >
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-xs font-semibold">
                    {(user.name || user.email).slice(0, 1).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user.name || user.email}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-900">{user.name}</span>
                    <span className="text-xs text-slate-500">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/map")} data-testid="menu-map-item">
                  <Map className="w-4 h-4 mr-2" /> Open Map
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing-item">
                  <CreditCard className="w-4 h-4 mr-2" /> Billing &amp; history
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                  <DropdownMenuItem onClick={() => navigate("/admin/dashboard")} data-testid="menu-admin-dashboard-item">
                    <Map className="w-4 h-4 mr-2" /> Admin · Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/bps")} data-testid="menu-admin-bps-item">
                    <Map className="w-4 h-4 mr-2" /> Admin · BPS data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/customers")} data-testid="menu-admin-customers-item">
                    <Map className="w-4 h-4 mr-2" /> Admin · Customers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/admin/engagements")} data-testid="menu-admin-engagements-item">
                    <Map className="w-4 h-4 mr-2" /> Admin · Engagements
                  </DropdownMenuItem>
                  </>
                )}
                {(user.role === "admin" || user.city_partner) && (
                  <DropdownMenuItem onClick={() => navigate("/cities-insights")} data-testid="menu-cities-insights">
                    <Sparkles className="w-4 h-4 mr-2 text-indigo-600" /> Cities Insights
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout-item" className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
